import {
  User,
  Phone,
  Package,
  MapPin,
  LogOut,
  Settings,
  Award,
  Loader2,
} from "lucide-react";
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../supabase';
import { useAuth } from '../context/AuthContext';
import { getUserProfile, getUserStats, submitWithdrawalRequest } from '../services/userProfile';
import { getOrdersByPhone } from '../services/orders';
import { signOut } from 'firebase/auth';
import { auth } from '../firebase';
import { toast } from 'react-toastify';

export default function Profile() {
  const [userData, setUserData] = useState(null);
  const [userStats, setUserStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [orders, setOrders] = useState([]);
  const [withdrawPhone, setWithdrawPhone] = useState('');
  const [paymentMethod, setPaymentMethod] = useState('INSTAPAY');
  const [withdrawPoints, setWithdrawPoints] = useState(100);
  const [isWithdrawing, setIsWithdrawing] = useState(false);
  const [showWithdrawForm, setShowWithdrawForm] = useState(false);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [verificationPassword, setVerificationPassword] = useState('');

  const handlePasswordVerification = async (e) => {
    e.preventDefault();
    try {
      const { data: userData } = await supabase
        .from('users')
        .select('password')
        .eq('id', user.id)
        .single();

      if (userData?.password === verificationPassword) {
        setShowPasswordModal(false);
        setVerificationPassword('');
        navigate('/settings');
      } else {
        toast.error('كلمة المرور غير صحيحة');
      }
    } catch (error) {
      console.error('Error verifying password:', error);
      toast.error('حدث خطأ أثناء التحقق من كلمة المرور');
    }
  };
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    const loadUserData = async () => {
      if (!user) {
        navigate('/auth');
        return;
      }

      try {
        // First fetch profile to obtain the phone number, then fetch stats by phone
        const profileResult = await getUserProfile(user.id);

        if (profileResult.status === 'success') {
          setUserData(profileResult.data);
        } else {
          toast.error(profileResult.message);
        }

        // If profile contains a phone, use it to fetch delivered-order count, points, and orders
        const phone = profileResult?.data?.phone;
        if (phone) {
          const [statsResult, ordersResult] = await Promise.all([
            getUserStats(phone),
            getOrdersByPhone(phone)
          ]);
          
          if (statsResult.status === 'success') {
            setUserStats(statsResult.data);
          } else {
            toast.error(statsResult.message);
          }

          if (ordersResult.status === 'success') {
            setOrders(ordersResult.data);
          }
        } else {
          // fallback: no phone available
          setUserStats({ orders: 0, points: 0 });
          setOrders([]);
        }
      } catch (error) {
        console.error('Error loading profile:', error);
        toast.error('حدث خطأ أثناء تحميل البيانات');
      } finally {
        setLoading(false);
      }
    };

    loadUserData();
  }, [user, navigate]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-950 to-slate-900 flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <Loader2 className="w-12 h-12 text-blue-500 animate-spin" />
          <p className="text-blue-300">جاري تحميل البيانات...</p>
        </div>
      </div>
    );
  }

  if (!userData) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-950 to-slate-900 flex items-center justify-center">
        <div className="bg-blue-900/50 p-8 rounded-2xl text-center">
          <p className="text-white text-xl">لم يتم العثور على بيانات المستخدم</p>
          <button
            onClick={() => navigate('/auth')}
            className="mt-4 px-6 py-2 bg-blue-600 text-white rounded-xl hover:bg-blue-700 transition-colors"
          >
            تسجيل الدخول
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-950 to-slate-900 p-2 sm:p-4 md:p-6 lg:p-8">
      <div className="max-w-4xl mt-10 w-5">
        {/* Header */}
        <div className="sticky top-0 z-10 bg-gradient-to-br from-slate-900 via-blue-950 to-slate-900 py-2 px-2 sm:px-0 -mx-2 sm:mx-0 mb-3 sm:mb-6">
          <div className="flex justify-between items-center">
            <button 
              onClick={() => setShowPasswordModal(true)} 
              className="mt-10  ml-auto p-2.5 bg-blue-900 hover:bg-blue-800 rounded-xl transition-colors border border-blue-700 backdrop-blur-sm"
            >
              <Settings className="w-5 h-5 text-blue-300" />
            </button>
            <h1 className="text-2xl sm:text-3xl font-bold text-white mr-1 mt-10">الإعدادات</h1>
          </div>
        </div>
          
          {/* Password Verification Modal */}
          {showPasswordModal && (
            <div className="fixed inset-0 bg-black backdrop-blur-sm flex items-center justify-center z-50 p-4">
              <div className="bg-gradient-to-br from-blue-900 to-blue-800 rounded-2xl p-6 w-full max-w-md border border-blue-700/30 shadow-2xl">
                <h3 className="text-xl text-white font-bold mb-4">التحقق من كلمة المرور</h3>
                <p className="text-blue-300 text-sm mb-6">يرجى إدخال كلمة المرور للوصول إلى الإعدادات</p>
                <form onSubmit={handlePasswordVerification} className="space-y-4">
                  <div>
                    <input
                      type="password"
                      value={verificationPassword}
                      onChange={(e) => setVerificationPassword(e.target.value)}
                      className="w-full bg-blue-900/50 border border-blue-700/30 rounded-xl px-4 py-3 text-white"
                      placeholder="أدخل كلمة المرور"
                      required
                    />
                  </div>
                  <div className="flex gap-3">
                    <button
                      type="button"
                      onClick={() => {
                        setShowPasswordModal(false);
                        setVerificationPassword('');
                      }}
                      className="flex-1 px-4 py-2 bg-blue-900/50 text-blue-300 rounded-xl hover:bg-blue-800/50 transition-colors"
                    >
                      إلغاء
                    </button>
                    <button
                      type="submit"
                      className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-xl hover:bg-blue-700 transition-colors"
                    >
                      تأكيد
                    </button>
                  </div>
                </form>
              </div>
            </div>
          )}
        </div>

        <div className="grid sm:grid-cols-1 md:grid-cols-3 gap-3 sm:gap-4 md:gap-6">
          {/* Profile Card */}
          <div className="md:col-span-1">
            <div className="bg-gradient-to-br from-blue-600 via-blue-700 to-blue-800 rounded-2xl p-4 sm:p-6 md:p-8 text-center shadow-2xl border border-blue-500/20">
              <div className="w-20 h-20 sm:w-24 sm:h-24 md:w-32 md:h-32 mx-auto rounded-full bg-white flex items-center justify-center text-blue-600 text-3xl sm:text-4xl md:text-5xl font-bold mb-3 sm:mb-4 shadow-xl ring-4 ring-blue-400/30">
                {userData.full_name?.charAt(0).toUpperCase() || 'U'}
              </div>
              <h2 className="text-white text-base sm:text-lg md:text-xl font-bold mb-2">
                {userData.full_name}
              </h2>
              <div className="inline-flex items-center gap-1.5 bg-blue-500/30 px-3 py-1.5 rounded-full border border-blue-400/30">
                <Award className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-blue-200" />
                <span className="text-blue-100 text-xs sm:text-sm">
                  {userData.account_type === 'user' ? 'عميل' : 'شريك توصيل'}
                </span>
              </div>
            </div>
          </div>

          {/* Info Cards */}
          <div className="md:col-span-2 space-y-4">
            <div className="bg-gradient-to-r from-blue-900/50 to-blue-800/50 rounded-2xl p-6 hover:from-blue-800/60 hover:to-blue-700/60 transition-all border border-blue-700/30 shadow-lg">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3 sm:gap-4">
                  <div className="w-10 h-10 sm:w-12 sm:h-12 bg-blue-600 rounded-lg sm:rounded-xl flex items-center justify-center shadow-lg">
                    <User className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
                  </div>
                  <div className="text-right">
                    <p className="text-blue-300 text-xs sm:text-sm mb-0.5 sm:mb-1">الاسم الكامل</p>
                    <p className="text-white text-sm sm:text-base md:text-lg font-semibold">
                      {userData.full_name}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-gradient-to-r from-blue-900/50 to-blue-800/50 rounded-xl sm:rounded-2xl p-4 sm:p-6 hover:from-blue-800/60 hover:to-blue-700/60 transition-all border border-blue-700/30 shadow-lg">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3 sm:gap-4">
                  <div className="w-10 h-10 sm:w-12 sm:h-12 bg-blue-500 rounded-lg sm:rounded-xl flex items-center justify-center shadow-lg">
                    <Phone className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
                  </div>
                  <div className="text-right">
                    <p className="text-blue-300 text-xs sm:text-sm mb-0.5 sm:mb-1">رقم الهاتف</p>
                    <p className="text-white text-sm sm:text-base md:text-lg font-semibold" dir="ltr">
                      {userData.phone}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
              <div className="bg-gradient-to-r from-blue-900/50 to-blue-800/50 rounded-xl sm:rounded-2xl p-4 sm:p-6 hover:from-blue-800/60 hover:to-blue-700/60 transition-all border border-blue-700/30 shadow-lg">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className="w-10 h-10 sm:w-12 sm:h-12 bg-blue-700 rounded-lg sm:rounded-xl flex items-center justify-center shadow-lg">
                      <Package className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
                    </div>
                    <div className="text-right">
                      <p className="text-blue-300 text-xs sm:text-sm mb-1">عدد الطلبات</p>
                      <p className="text-white text-base sm:text-lg font-semibold">
                        {userStats?.orders || 0}
                      </p>
                      {userStats?.unusedOrders > 0 && (
                        <p className="text-green-300 text-xs mt-1">
                          {userStats.unusedOrders} طلب متاح للنقاط
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-gradient-to-r from-blue-900/50 to-blue-800/50 rounded-2xl p-6 hover:from-blue-800/60 hover:to-blue-700/60 transition-all border border-blue-700/30 shadow-lg">
                <div className="flex flex-col gap-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 bg-yellow-600 rounded-xl flex items-center justify-center shadow-lg">
                        <Award className="w-6 h-6 text-white" />
                      </div>
                      <div className="text-right">
                        <p className="text-blue-300 text-sm mb-1">النقاط</p>
                        <p className="text-white text-lg font-semibold">
                          {userStats?.points || 0}
                        </p>
                      </div>
                    </div>
                    <button
                      onClick={() => setShowWithdrawForm(!showWithdrawForm)}
                      className="px-3 sm:px-4 py-1.5 sm:py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg sm:rounded-xl transition-colors text-xs sm:text-sm font-semibold"
                    >
                      سحب النقاط
                    </button>
                  </div>

                  {/* Withdrawal Form */}
                  {showWithdrawForm && (
                    <div className="pt-3 sm:pt-4 border-t border-blue-700/30 space-y-3 sm:space-y-4">
                      <div>
                        <label className="block text-blue-300 text-xs sm:text-sm mb-1.5 sm:mb-2">عدد النقاط المراد سحبها</label>
                          <div className="flex gap-1.5 sm:gap-2 flex-wrap">
                          {[100, 200, 300, 500, 1000].map((points) => (
                            <button
                              key={points}
                              onClick={() => {
                                if (points <= (userStats?.points || 0)) {
                                  setWithdrawPoints(points);
                                } else {
                                  toast.error('عدد النقاط المطلوب أكبر من رصيدك الحالي');
                                }
                              }}
                              className={`px-2 sm:px-3 md:px-4 py-1.5 sm:py-2 rounded-lg sm:rounded-xl text-xs sm:text-sm font-semibold transition-colors ${
                                withdrawPoints === points
                                  ? 'bg-blue-600 text-white'
                                  : points <= (userStats?.points || 0)
                                  ? 'bg-blue-900/50 text-blue-300 hover:bg-blue-900'
                                  : 'bg-gray-800/50 text-gray-500 cursor-not-allowed'
                              }`}
                            >
                              {points} نقطة
                            </button>
                          ))}
                        </div>
                        <input
                          type="number"
                          value={withdrawPoints}
                          onChange={(e) => {
                            let value = parseInt(e.target.value);
                            // تقريب القيمة إلى أقرب مضاعف من 100
                            value = Math.round(value / 100) * 100;
                            
                            if (value > (userStats?.points || 0)) {
                              toast.error('عدد النقاط المطلوب أكبر من رصيدك الحالي');
                              return;
                            }
                            if (value <= 0) {
                              toast.error('يجب أن يكون عدد النقاط أكبر من صفر');
                              return;
                            }
                            setWithdrawPoints(value);
                          }}
                          min="100"
                          max={userStats?.points || 0}
                          step="100"
                          className="w-full mt-2 bg-blue-900 border border-blue-700/30 rounded-xl px-4 py-2 text-white"
                        />
                      </div>
                      <div>
                        <label className="block text-blue-300 text-sm mb-2">رقم الهاتف للتحويل</label>
                        <input
                          type="tel"                                                
                          className="w-full bg-blue-900 border border-blue-700/30 rounded-xl px-4 py-2 text-white"
                          placeholder="ادخل رقم الهاتف"
                        />
                      </div>
                      <div>
                        <label className="block text-blue-300 text-sm mb-2">طريقة التحويل</label>
                        <select
                          value={paymentMethod}
                          onChange={(e) => setPaymentMethod(e.target.value)}
                          className="w-full bg-blue-900 border border-blue-700/30 rounded-xl px-4 py-2 text-white"
                        >
                          <option value="INSTAPAY">Instapay</option>
                          <option value="محفظة إلكترونية">محفظة إلكترونية</option>
                        </select>
                      </div>
                      <button
                        onClick={async () => {
                          if (!withdrawPhone) {
                            toast.error('يرجى إدخال رقم الهاتف للتحويل');
                            return;
                          }
                          
                          try {
                            setIsWithdrawing(true);
                            const withdrawalData = {
                              customer_phone: userData.phone,
                              full_name: userData.full_name,
                              withdraw_phone_number: withdrawPhone,
                              pay_method_type: paymentMethod,
                              points_amount: withdrawPoints
                            };
                            
                            const response = await submitWithdrawalRequest(withdrawalData);
                            
                            if (response.status === 'error') {
                              throw new Error(response.message);
                            }
                            
                            toast.success('تم تقديم طلب السحب بنجاح');
                            setWithdrawPhone('');
                            setPaymentMethod('INSTAPAY');
                            setShowWithdrawForm(false);
                          } catch (error) {
                            console.error('Error submitting withdrawal request:', error);
                            toast.error('حدث خطأ أثناء تقديم طلب السحب');
                          } finally {
                            setIsWithdrawing(false);
                          }
                        }}
                        disabled={isWithdrawing}
                        className={`w-full ${
                          isWithdrawing 
                            ? 'bg-blue-800 cursor-not-allowed' 
                            : 'bg-blue-600 hover:bg-blue-700'
                        } text-white font-semibold py-2.5 sm:py-3 rounded-xl transition-colors flex items-center justify-center gap-2 text-sm sm:text-base`}
                      >
                        {isWithdrawing && <Loader2 className="w-4 h-4 animate-spin" />}
                        <span className="inline-block pt-0.5">{isWithdrawing ? 'جاري تقديم الطلب...' : 'تحويل النقاط'}</span>
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="bg-gradient-to-r from-blue-900/50 to-blue-800/50 rounded-2xl p-6 hover:from-blue-800/60 hover:to-blue-700/60 transition-all border border-blue-700/30 shadow-lg">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-cyan-600 rounded-xl flex items-center justify-center shadow-lg">
                    <MapPin className="w-6 h-6 text-white" />
                  </div>
                  <div className="text-right">
                    <p className="text-blue-300 text-sm mb-1">العنوان</p>
                    <p className="text-white text-lg font-semibold">
                      {userData.address}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <button
              onClick={async () => {
                try {
                  await signOut(auth);
                  logout();
                  navigate('/auth');
                  toast.success('تم تسجيل الخروج بنجاح');
                } catch (error) {
                  console.error('Error signing out:', error);
                  toast.error('حدث خطأ أثناء تسجيل الخروج');
                }
              }}
              className="w-full flex justify-center items-center gap-3 bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-500 hover:to-blue-600 text-white py-4 rounded-2xl transition-all font-semibold text-lg shadow-xl border border-blue-500/30"
            >
              <LogOut className="w-6 h-6" />
              تسجيل خروج
            </button>
          </div>

          {/* Previous Orders Section */}
          <div className="flex flex-col md:col-span-3 mt-4 sm:mt-6 md:mt-8">
            <h2 className="text-lg sm:text-xl md:text-2xl font-bold text-white mb-3 sm:mb-4 md:mb-6">طلباتي السابقة</h2>
            <div className="space-y-3">
              {orders.length === 0 ? (
                <div className="bg-blue-900/50 rounded-lg sm:rounded-xl p-4 text-center border border-blue-700/30">
                  <p className="text-blue-200 text-sm">لا توجد طلبات سابقة</p>
                </div>
              ) : (
                orders.map((order) => (
                  <div key={order.id} className="bg-gradient-to-r from-blue-900/50 to-blue-800/50 rounded-xl sm:rounded-2xl p-4 sm:p-6 border border-blue-700/30 shadow-lg hover:from-blue-800/60 hover:to-blue-700/60 transition-all">
                    <div className="flex flex-col md:flex-row gap-4 md:gap-6">
                      <div className="flex-1 space-y-3">
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 bg-blue-900/50 rounded-lg flex items-center justify-center">
                            <span className="text-blue-300 text-sm">⌚</span>
                          </div>
                          <div>
                            <p className="text-blue-300 text-xs mb-0.5">الوقت والتاريخ</p>
                            <p className="text-white text-sm">
                              {new Date(order.created_at).toLocaleTimeString('ar-EG', {
                                hour: 'numeric',
                                minute: 'numeric',
                                hour12: true
                              })}
                              {' - '}
                              {new Date(order.created_at).toLocaleDateString('ar-EG', {
                                year: 'numeric',
                                month: 'numeric',
                                day: 'numeric'
                              })}
                            </p>
                          </div>
                        </div>
                      </div>
                      <div className="flex-1 space-y-3">
                        <div className="flex items-start gap-3">
                          <div className="w-8 h-8 bg-blue-900/50 rounded-lg flex items-center justify-center mt-0.5">
                            <MapPin className="w-4 h-4 text-blue-300" />
                          </div>
                          <div>
                            <div className="mb-2">
                              <p className="text-blue-300 text-xs mb-0.5">من</p>
                              <p className="text-white text-sm line-clamp-2">{order.pickup_address}</p>
                            </div>
                            <div>
                              <p className="text-blue-300 text-xs mb-0.5">إلى</p>
                              <p className="text-white text-sm line-clamp-2">{order.delivery_address || 'غير محدد'}</p>
                            </div>
                          </div>
                        </div>
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <div className="w-8 h-8 bg-blue-900/50 rounded-lg flex items-center justify-center">
                            <Package className="w-4 h-4 text-blue-300" />
                          </div>
                          <div>
                            <p className="text-blue-300 text-xs mb-1">الحالة</p>
                            <span className={`px-2.5 py-1 rounded-full text-xs ${
                              order.status === 'تم التوصيل' ? 'bg-green-500/20 text-green-300 border border-green-500/30' :
                              order.status === 'قيد الانتظار' ? 'bg-yellow-500/20 text-yellow-300 border border-yellow-500/30' :
                              'bg-blue-500/20 text-blue-300 border border-blue-500/30'
                            }`}>
                              {order.status}
                            </span>
                          </div>
                        </div>
                        {order.notes && (
                          <div className="flex items-start gap-3">
                            <div className="w-8 h-8 bg-blue-900/50 rounded-lg flex items-center justify-center mt-0.5">
                              <span className="text-blue-300 text-sm">📝</span>
                            </div>
                            <div>
                              <p className="text-blue-300 text-xs mb-0.5">ملاحظات</p>
                              <p className="text-white text-sm">{order.notes}</p>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      </div>
  );
}
